import { useState, useEffect, useMemo } from "react";
import tw from "tailwind-styled-components";
import { InputGroup, SubmitBtn, ActionSection, SideCancelBtn, Wrapper, FieldWrappers, ScrollableContent, ModalHeader } from "../Styles/InputStyles";
import { MdClose, MdEdit } from "react-icons/md";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { ProfileSection, UserInfo } from "../Styles/PageStyles";
import { capitalizeFirstLetter } from "../../helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import moment from "moment";
import Config from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import TimeBoxesInput from "../Common/TimeBoxesInput";

const EditLead = ({ handleClose, selectedData, editLeadLoading, editLeadMutate, setViewDetail }) => {
    const [open, setOpen] = useState(false);
    const [height, setHeight] = useState(window.innerHeight);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const statusOptions = [
        { value: Config.LEAD_STATUS.NEW, label: 'New' },
        { value: Config.LEAD_STATUS.FOLLOW_UP, label: 'Follow Up' },
        { value: Config.LEAD_STATUS.VISIT_SCHEDULED, label: 'Visit Scheduled' },
        { value: Config.LEAD_STATUS.VISIT_DONE, label: 'Visit Done' },
        { value: Config.LEAD_STATUS.NOT_INTERESTED, label: 'Not Interested' },
        { value: Config.LEAD_STATUS.CONTACTED, label: 'Contacted' },
        { value: Config.LEAD_STATUS.INTERESTED, label: 'Interested' },
        { value: Config.LEAD_STATUS.INVALID, label: 'Invalid' },
        { value: Config.LEAD_STATUS.ON_HOLD, label: 'On Hold' },
        { value: Config.LEAD_STATUS.NOT_ANSWERED, label: 'Not Answered' },
        { value: Config.LEAD_STATUS.LOST, label: 'Lost' },
        { value: Config.LEAD_STATUS.PAID_TOKEN, label: 'Token Paid' },
    ];

    const initialValues = useMemo(() => ({
        name: selectedData?.name || "N/A",
        propertyName: selectedData?.propertyName || "N/A",
        status: selectedData?.status?.toString() || "",
        remark: selectedData?.remarks || "",
        visitDate: selectedData?.visitDateTime
            ? selectedData.visitDateTime.split(" ")[0]
            : "",
        visitTime: selectedData?.visitDateTime
            ? moment(
                selectedData.visitDateTime.split(" ")[1]?.slice(0, 5),
                "HH:mm"
            ).format("h:mm A")
            : "",
    }), [selectedData]);

    const handleSubmit = (values) => {
        const statusNum = Number(values.status);

        const needsDateTime =
            statusNum === Number(Config.LEAD_STATUS.FOLLOW_UP) ||
            statusNum === Number(Config.LEAD_STATUS.VISIT_SCHEDULED) ||
            statusNum === Number(Config.LEAD_STATUS.VISIT_DONE);

        if (!values.status) {
            toast.error("Status is required");
            return;
        }

        if (needsDateTime && (!values.visitDate || !values.visitTime)) {
            toast.error("Date and time are required for this status");
            return;
        }

        const visitDateTime =
            needsDateTime && values.visitDate && values.visitTime
                ? `${values.visitDate} ${moment(values.visitTime, "h:mm A").format("HH:mm")}`
                : undefined;

        const payload = {
            id: selectedData?.id,
            visitDateTime,
            remarks: values.remark,
            status: statusNum,
        };
        editLeadMutate(payload);
    };

    const visitDateTimeLabel = {
        [Config.LEAD_STATUS.FOLLOW_UP]: "Follow Up Date & Time *",
        [Config.LEAD_STATUS.VISIT_SCHEDULED]: "Visit Date & Time *",
        [Config.LEAD_STATUS.VISIT_DONE]: "Follow Up Date & Time *",
    };

    const getStatusLabel = (statusValue) => {
        const status = statusOptions.find(s => s.value === Number(statusValue));
        return status ? status.label : "Select Status";
    };

    const statusesRequiringDateTime = [
        Config.LEAD_STATUS.FOLLOW_UP,
        Config.LEAD_STATUS.VISIT_SCHEDULED,
        Config.LEAD_STATUS.VISIT_DONE
    ];

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Lead</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <ProfileSection>
                <UserInfo>
                    <span className="font-medium">{capitalizeFirstLetter(selectedData?.name)}</span>
                    <span className="text-sm text-gray-600">
                        {selectedData?.linkedProperties?.length > 0
                            ? selectedData.linkedProperties.map((prop, index) => (
                                <span key={prop.id}>
                                    {prop.name}
                                    {index !== selectedData.linkedProperties.length - 1 && ', '}
                                </span>
                            ))
                            : <span className="text-gray-400">-</span>
                        }
                    </span>
                </UserInfo>
            </ProfileSection>
            <Content>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, handleChange, handleBlur, errors, touched, setFieldValue, dirty }) => (
                        <Form className="flex flex-col h-full justify-between">
                            <ScrollableContent className="max-h-[calc(100vh-100px)]">
                                <Wrapper className="mt-0">
                                    <InputGroup>
                                        <Label htmlFor="status">Status *</Label>
                                        <FieldWrappers onClick={() => setIsStatusModalOpen(true)}>
                                            <Field
                                                type="text"
                                                name="statusDisplay"
                                                id="statusDisplay"
                                                autoComplete="off"
                                                readOnly
                                                value={values.status ? getStatusLabel(values.status) : 'Select Status'}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        <div className=" mt-1">
                                            {errors.status && touched.status && (
                                                <ErrorText>{errors.status}</ErrorText>
                                            )}
                                        </div>
                                    </InputGroup>

                                    {values.status && statusesRequiringDateTime.includes(Number(values.status)) && (
                                        <>
                                            <InputGroup>
                                                <Label htmlFor="visitDate">
                                                    {visitDateTimeLabel[Number(values.status)]?.replace(" & Time", "") || "Date *"}
                                                </Label>
                                                <FieldWrappers>
                                                    <DatePicker
                                                        open={open}
                                                        onInputClick={() => setOpen(true)}
                                                        onClickOutside={() => setOpen(false)}
                                                        selected={
                                                            values.visitDate
                                                                ? new Date(`${values.visitDate}T00:00:00`)
                                                                : null
                                                        }
                                                        onChange={(date) => {
                                                            setFieldValue(
                                                                "visitDate",
                                                                date ? moment(date).format("YYYY-MM-DD") : ""
                                                            );
                                                            setOpen(false);
                                                        }}
                                                        dateFormat="dd MMM yyyy"
                                                        className="w-full cursor-pointer"
                                                        placeholderText="Select date"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        wrapperClassName="w-full"
                                                        name="visitDate"
                                                        id="visitDate"
                                                        autoComplete="off"
                                                        minDate={new Date()}
                                                        portalId="root-portal"
                                                        popperPlacement="bottom-start"
                                                        onFocus={(e) => e.target.blur()}
                                                    />
                                                </FieldWrappers>
                                                {errors.visitDate && touched.visitDate && (
                                                    <ErrorText>{errors.visitDate}</ErrorText>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label>Time *</Label>
                                                <TimeBoxesInput
                                                    value={values.visitTime}
                                                    onChange={(val) => setFieldValue("visitTime", val)}
                                                />
                                                {errors.visitTime && touched.visitTime && (
                                                    <ErrorText>{errors.visitTime}</ErrorText>
                                                )}
                                            </InputGroup>
                                        </>
                                    )}

                                    <InputGroup>
                                        <Label htmlFor="remark">Notes</Label>
                                        <FieldWrapper>
                                            <Field
                                                as={Textarea}
                                                id="remark"
                                                name="remark"
                                                rows={5}
                                                placeholder="Enter Remarks (Optional)"
                                            />
                                        </FieldWrapper>
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>
                            {/* Actions */}
                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={editLeadLoading || !dirty}
                                    className="disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {editLeadLoading ? "Submitting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>

                            <SelectOptionModal
                                isOpen={isStatusModalOpen}
                                onClose={() => setIsStatusModalOpen(false)}
                                title="Select Status"
                                options={statusOptions}
                                selectedValue={values?.status ? Number(values.status) : ""}
                                onSelect={(value) => {
                                    setFieldValue('status', String(value), true);
                                    if (!statusesRequiringDateTime.includes(Number(value))) {
                                        setFieldValue('visitDate', '');
                                        setFieldValue('visitTime', '');
                                    }
                                    setIsStatusModalOpen(false);
                                }}
                                searchPlaceholder="Search status..."
                            />
                        </Form>
                    )}
                </Formik>
            </Content>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y break-words overflow-hidden`;
const FieldWrapper = tw.div`w-full`;
const Content = tw.div`p-4 flex flex-col gap-4 mb-10`;
const Label = tw.label`text-sm text-gray-500 font-normal`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default EditLead;